import React from 'react';
import { getImageProxyUrl } from './imageProxyUtil';
import { ChevronDown, ChevronUp, Expand, Minimize2 } from 'lucide-react';
import { useAdaptiveTitle } from '../hooks/useAdaptiveTitle';

/**
 * Componente per la card di una serie TV
 */
function SeriesCard({ 
  series, 
  dark, 
  isExpanded, 
  onToggleExpansion, 
  onSelectEpisode,
  globalSearchTerm = '',
  isGlobalMode = false,
  toggleFavorite = () => {},
  isFavorite = () => false
}) {
  const { titleRef, titleClassName, containerClassName } = useAdaptiveTitle(series.name, 32);

  const handleCardClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    onToggleExpansion(series.seriesKey);
  };

  const handlePlayFirstEpisode = (e) => {
    e.preventDefault();
    e.stopPropagation();
    // Riproduci il primo episodio della serie
    const firstEpisode = series.episodes[0];
    onSelectEpisode(firstEpisode);
  };

  return (
    <div
      className={`group relative backdrop-blur-sm border-2 rounded-xl transition-all duration-300 cursor-pointer overflow-hidden ${
        isExpanded 
          ? dark 
            ? 'bg-purple-500/20 border-purple-400/60 shadow-xl shadow-purple-500/30' 
            : 'bg-purple-100/80 border-purple-400/60 shadow-xl shadow-purple-500/30'
          : dark 
            ? 'bg-gray-900/30 hover:bg-gray-800/50 border-purple-500/50 hover:border-purple-400/70 hover:shadow-2xl hover:shadow-purple-500/20 hover:scale-105' 
            : 'bg-white/90 border-purple-400/60 hover:bg-white hover:border-purple-500/80 hover:shadow-xl hover:shadow-purple-500/30 hover:scale-105'
      }`}
      style={{ width: '170px', maxWidth: '170px', minWidth: '140px' }}
      onClick={handleCardClick}
    >
      {/* Image Container - Uniforme ai canali normali */}
      <div className="relative aspect-video overflow-hidden">
        {/* Logo della serie */}
        {series.tvgLogo ? (
          <img 
            src={getImageProxyUrl(series.tvgLogo)} 
            alt="Serie Logo" 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              if (e.target.src.includes('/proxy/image') && series.originalLogoUrl && !e.target.hasRetried) {
                e.target.hasRetried = true;
                const separator = e.target.src.includes('?') ? '&' : '?';
                e.target.src = e.target.src + separator + 'retry=' + Date.now();
              } else {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }
            }}
          />
        ) : null}
        
        {/* Fallback placeholder */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-4xl"
          style={{ display: series.tvgLogo ? 'none' : 'flex' }}
        >
          📺
        </div>
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Favorites Toggle - Gestisce tutti gli episodi della serie */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            // Controlla se tutti gli episodi sono già nei preferiti
            const allFavorited = series.episodes.every(episode => isFavorite(episode));
            
            // Se tutti sono preferiti, rimuovi solo quelli che sono preferiti
            // Se non tutti sono preferiti, aggiungi solo quelli che non sono preferiti
            series.episodes.forEach(episode => {
              const isCurrentlyFavorite = isFavorite(episode);
              if (allFavorited && isCurrentlyFavorite) {
                // Rimuovi dai preferiti solo se è attualmente preferito
                toggleFavorite(episode);
              } else if (!allFavorited && !isCurrentlyFavorite) {
                // Aggiungi ai preferiti solo se non è attualmente preferito
                toggleFavorite(episode);
              }
            });
          }}
          className="absolute top-2 left-2 p-1 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-4 h-4 ${series.episodes.every(episode => isFavorite(episode)) ? 'text-red-500' : 'text-white'}`}
            fill={series.episodes.every(episode => isFavorite(episode)) ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>

        {/* Serie Badge - Spostato in alto a destra come i gruppi */}
        <div className="absolute top-2 right-2">
          <span className="bg-purple-500/80 backdrop-blur-sm text-white px-1.5 py-0.5 rounded-full font-bold" style={{ fontSize: '0.5rem' }}>
            SERIE
          </span>
        </div>

        {/* Expansion Toggle - Posizionato in basso a destra */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onToggleExpansion(series.seriesKey);
          }}
          className={`absolute bottom-2 right-2 w-8 h-8 rounded-full flex items-center justify-center font-bold border shadow-lg transition-all duration-300 backdrop-blur-sm ${
            isExpanded
              ? 'bg-gradient-to-r from-red-500/80 to-pink-500/80 text-white border-red-400/50 hover:from-red-600/90 hover:to-pink-600/90' 
              : 'bg-gradient-to-r from-blue-500/80 to-purple-500/80 text-white border-blue-400/50 hover:from-blue-600/90 hover:to-purple-600/90'
          }`}>
          {isExpanded ? <Minimize2 size={14} /> : <Expand size={14} />}
        </button>
      </div>
      
      {/* Serie Info - Uniforme ai canali normali */}
      <div className="p-3">
        <div className={containerClassName} style={{ maxHeight: '32px', minHeight: '32px' }}>
          <h3 
            ref={titleRef}
            className={`${titleClassName} font-semibold text-white mb-1 group-hover:text-purple-400 transition-colors`}
          >
            {isGlobalMode && globalSearchTerm && series.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ? (
              series.name.split(new RegExp(`(${globalSearchTerm})`, 'gi')).map((part, i) => 
                part.toLowerCase() === globalSearchTerm.toLowerCase() ? (
                  <mark key={i} className={`${dark ? 'bg-yellow-400/30 text-yellow-200' : 'bg-yellow-200 text-yellow-800'} px-0.5 rounded`}>
                    {part}
                  </mark>
                ) : part
              )
            ) : (
              series.name
            )}
          </h3>
        </div>

        {/* Nome file M3U sotto il titolo */}
        {series.episodes[0]?.playlistName && (
          <p className="text-gray-400 text-xs truncate mt-1">
            {series.episodes[0].playlistName}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Componente per l'header di una stagione
 */
function SeasonHeader({ season, dark, isGlobalMode }) {
  return (
    <div className={`col-span-full mb-2 backdrop-blur-sm border rounded-lg p-2 ${
      dark 
        ? 'bg-indigo-500/10 border-indigo-400/30 text-indigo-200' 
        : 'bg-indigo-100 border-indigo-300 text-indigo-800'
    }`}>
      <div className="flex items-center gap-2">
        <span style={{ fontSize: '0.9rem' }}>🎬</span>
        <span className="font-bold" style={{ fontSize: '0.8rem' }}>{season.name}</span>
        <span className={`px-1.5 py-0.5 rounded-full ${
          dark 
            ? 'bg-indigo-400/20 text-indigo-100' 
            : 'bg-indigo-200 text-indigo-700'
        }`} style={{ fontSize: '0.6rem' }}>
          {season.episodeCount} episodi
        </span>
      </div>
    </div>
  );
}

/**
 * Componente per un episodio di una serie
 */
function EpisodeCard({ 
  episode, 
  dark, 
  onSelect, 
  globalSearchTerm = '', 
  isGlobalMode = false,
  toggleFavorite = () => {},
  isFavorite = () => false
}) {
  const { titleRef, titleClassName, containerClassName } = useAdaptiveTitle(episode.name, 32);
  return (
    <div
      className={`group relative backdrop-blur-sm border-2 rounded-xl transition-all duration-300 cursor-pointer overflow-hidden ${
        dark 
          ? 'bg-gray-900/30 hover:bg-gray-800/50 border-cyan-500/50 hover:border-cyan-400/70 hover:shadow-2xl hover:shadow-cyan-500/20 hover:scale-105' 
          : 'bg-white/90 border-cyan-400/60 hover:bg-white hover:border-cyan-500/80 hover:shadow-xl hover:shadow-cyan-500/30 hover:scale-105'
      }`}
      style={{ width: '170px', maxWidth: '170px', minWidth: '140px' }}
      onClick={() => onSelect(episode)}
    >
      {/* Image Container - Stesso stile delle altre card */}
      <div className="relative aspect-video overflow-hidden">
        {episode.tvgLogo ? (
          <img 
            src={getImageProxyUrl(episode.tvgLogo)} 
            alt="Episodio Logo" 
            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
            onError={(e) => {
              if (e.target.src.includes('/proxy/image') && episode.originalLogoUrl && !e.target.hasRetried) {
                e.target.hasRetried = true;
                const separator = e.target.src.includes('?') ? '&' : '?';
                e.target.src = e.target.src + separator + 'retry=' + Date.now();
              } else {
                e.target.style.display = 'none';
                if (e.target.nextSibling) {
                  e.target.nextSibling.style.display = 'flex';
                }
              }
            }}
          />
        ) : null}
        
        {/* Fallback placeholder */}
        <div 
          className="absolute inset-0 bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center text-4xl"
          style={{ display: episode.tvgLogo ? 'none' : 'flex' }}
        >
          📺
        </div>
        
        {/* Overlay gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
        
        {/* Favorites Toggle - Stesso stile delle altre card */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            toggleFavorite(episode);
          }}
          className="absolute top-2 left-2 p-1 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-sm transition-all duration-200"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`w-4 h-4 ${isFavorite(episode) ? 'text-red-500' : 'text-white'}`}
            fill={isFavorite(episode) ? 'currentColor' : 'none'}
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </button>

        {/* Episodio Badge - Solo scritta "EP" per distinguerlo */}
        <div className="absolute top-2 right-2">
          <span className="bg-cyan-500/80 backdrop-blur-sm text-white px-1.5 py-0.5 rounded-full font-bold" style={{ fontSize: '0.5rem' }}>
            EP
          </span>
        </div>
      </div>
      
      {/* Episode Info - Stesso stile delle altre card */}
      <div className="p-3">
        <div className={containerClassName} style={{ maxHeight: '32px', minHeight: '32px' }}>
          <h3 
            ref={titleRef}
            className={`${titleClassName} font-semibold mb-1 group-hover:text-cyan-400 transition-colors ${
              dark ? 'text-white' : 'text-gray-800'
            }`}
          >
            {isGlobalMode && globalSearchTerm && episode.name.toLowerCase().includes(globalSearchTerm.toLowerCase()) ? (
              episode.name.split(new RegExp(`(${globalSearchTerm})`, 'gi')).map((part, i) => 
                part.toLowerCase() === globalSearchTerm.toLowerCase() ? (
                  <mark key={i} className={`${dark ? 'bg-yellow-400/30 text-yellow-200' : 'bg-yellow-200 text-yellow-800'} px-0.5 rounded`}>
                    {part}
                  </mark>
                ) : part
              )
            ) : (
              episode.name
            )}
          </h3>
        </div>

        {/* Gruppo - Stesso stile delle altre card */}
        <p className={`text-xs truncate ${dark ? 'text-gray-400' : 'text-gray-600'}`}>
          {isGlobalMode && '🌐 '}{episode.group}
        </p>
      </div>
    </div>
  );
}

export { SeriesCard, SeasonHeader, EpisodeCard };
